import {
  AutoStack,
  DayPickerStrings,
  FlexSpinner,
  FormikGraphQLDatePicker,
  onFormatDate,
} from 'components'
import { FormikTagPicker } from 'components/TagPicker/FormikTagPicker'
import { FieldArray } from 'formik'
import {
  FormikDropdown,
  FormikSpinButton,
  FormikTextField,
} from 'formik-office-ui-fabric-react'
import { FieldStack, FieldStackItem, FormStack } from 'forms/FormStack'
import { useCurrentFormik } from 'hooks/useCurrentFormikContext'
import { Proving } from 'model'
import {
  DayOfWeek,
  DefaultButton,
  Dropdown,
  IDropdownOption,
  ITag,
  IconButton,
  Label,
  Stack,
  useTheme,
} from 'office-ui-fabric-react'
import { Position } from 'office-ui-fabric-react/lib/utilities/positioning'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import shortid from 'shortid'
import {
  useDangerousGoodsQuery,
  useMaterialsQuery,
} from 'views/ProvingDetailsView/hooks'
import * as Yup from 'yup'

export interface ProvingDetailsAddMaterialValues {
  material: string
  amount: string
  orderId: string
  annotation: string
  isDangerousGood: boolean
  dangerousGood?: {
    unNumber: string
  }
}

const tagPickerStyle = {
  root: {
    backgroundColor: 'white',
  },
}

export class ProvingDetailsMaterialsFormSchema {
  constructor() {
    return Yup.object().shape({
      materials: Yup.array().of(
        Yup.object().shape({
          material: Yup.string().required('Material is required'),
          dateFrom: Yup.date().required('Date from is required'),
          dateTo: Yup.date()
            .required('Date to is required')
            .when('dateFrom', (dateFrom, schema) =>
              dateFrom
                ? schema.min(dateFrom, 'Date to must be later than date from')
                : schema
            ),
          amount: Yup.string(),
          orderId: Yup.string().required('Order ID is required'),
          annotation: Yup.string().trim(),
          isDangerousGood: Yup.boolean().required(
            'Is Dangerous Good is required'
          ),
          dangerousGood: Yup.object().when('isDangerousGood', {
            is: true,
            then: Yup.object()
              .shape({
                unNumber: Yup.string().required(
                  'UN Number is required when the material is a dangerous good'
                ),
              })
              .required(
                'Dangerous Good details are required when the material is a dangerous good'
              ),
            otherwise: Yup.object().nullable(true),
          }),
        })
      ),
    })
  }
}

export const ProvingDetailsAddMaterialForm: React.FC<{
  orderIds: any[]
  proving: Proving
}> = ({ orderIds, proving }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { data, loading, error } = useMaterialsQuery()
  const { data: dangerousGoodsData } = useDangerousGoodsQuery()
  const { setFieldValue } = useCurrentFormik()

  const dangerousGoods: ITag[] = React.useMemo(() => {
    return (
      dangerousGoodsData?.dangerousGoods?.map((dangerousGood) => ({
        key: dangerousGood.unNumber,
        name: `${dangerousGood.unNumber}${
          dangerousGood?.description ? ` - ${dangerousGood.description}` : ''
        }`,
      })) || []
    )
  }, [dangerousGoodsData])

  const materials: ITag[] =
    data && !loading
      ? data.materials.map((material) => ({
          key: material._id,
          name: material.label,
        }))
      : []

  const sortedMaterials = materials.sort((a, b) =>
    a.name < b.name ? -1 : a.name > b.name ? 1 : 0
  )

  const handleUNNumberChange = (item: ITag, index: number) => {
    const dangerousGood = dangerousGoodsData?.dangerousGoods.find(
      (dg) => dg.unNumber === item.key
    )
    setFieldValue(
      `materials[${index}].dangerousGood.description`,
      dangerousGood?.description
    )
  }

  const handleIsDangerousGoodChange = (
    index: number,
    option?: IDropdownOption
  ) => {
    const value = option?.key as number
    setFieldValue(
      `materials[${index}].isDangerousGood`,
      value === -1 ? undefined : Boolean(value)
    )

    if (value === -1 || value === 0)
      setFieldValue(`materials[${index}].dangerousGood`, undefined)
  }

  return (
    <FormStack>
      <FlexSpinner loading={loading} error={error}>
        <>
          <Label>{t('field.material.addMaterial')}</Label>
          <FieldArray name="materials">
            {({ form, name, ...helpers }) => {
              const values: {
                material: string
                orderId: string
                amount: string
                transport: { transportType: string }
                annotation: string
                key: string
                isDangerousGood: boolean
              }[] = form.values.materials
              if (values.length === 0) {
                helpers.push({
                  material: '',
                  amount: 1,
                  orderId: '',
                  annotation: '',
                  transport: { transportType: '' },
                  key: shortid.generate(),
                })
              }

              return (
                <AutoStack tokens={{ childrenGap: 15 }}>
                  {values.map((x: any, i: number) => (
                    <Stack
                      key={x.key}
                      style={{
                        backgroundColor:
                          i % 2 ? theme.palette.neutralLighter : undefined,
                        padding: 10,
                      }}
                    >
                      <FieldStack horizontal={true} key={x.key}>
                        <FieldStackItem
                          name={`materials[${i}].material`}
                          render={(fieldProps) => (
                            <FormikTagPicker
                              label={t('field.materialType.label')}
                              styles={tagPickerStyle}
                              {...fieldProps}
                              tags={sortedMaterials}
                              required={true}
                            />
                          )}
                        />

                        <FieldStackItem
                          name={`materials[${i}].dateFrom`}
                          label={t('field.dateFrom.label')}
                          isRequired={true}
                          component={FormikGraphQLDatePicker}
                          firstDayOfWeek={DayOfWeek.Monday}
                          strings={DayPickerStrings}
                          showWeekNumbers={true}
                          formatDate={onFormatDate}
                          initialPickerDate={new Date(proving.dateFrom)}
                        />
                        <FieldStackItem
                          name={`materials[${i}].dateTo`}
                          label={t('field.dateTo.label')}
                          isRequired={true}
                          component={FormikGraphQLDatePicker}
                          firstDayOfWeek={DayOfWeek.Monday}
                          strings={DayPickerStrings}
                          showWeekNumbers={true}
                          formatDate={onFormatDate}
                          today={new Date(proving.dateTo)}
                          initialPickerDate={new Date(proving.dateFrom)}
                        />
                        <FieldStackItem
                          name={`materials[${i}].amount`}
                          grow={false}
                          render={(fieldProps) => (
                            <FormikSpinButton
                              label={t('field.amount.label')}
                              labelPosition={Position.top}
                              max={10}
                              min={1}
                              {...fieldProps}
                            />
                          )}
                        />
                        <FieldStackItem
                          name={`materials[${i}].orderId`}
                          disableShrink={false}
                          render={(fieldProps) => (
                            <FormikDropdown
                              label={t('field.orderId.label')}
                              {...fieldProps}
                              options={[
                                ...orderIds.map(
                                  (orderId) =>
                                    ({
                                      key: orderId.orderId,
                                      text: `${orderId.orderId} - ${
                                        orderId.annotation
                                          ? `${orderId.annotation} `
                                          : ''
                                      }(${orderId.percentage} %)`,
                                    } as IDropdownOption)
                                ),
                              ]}
                              required={true}
                            />
                          )}
                        />
                        <FieldStackItem
                          name={`materials[${i}].annotation`}
                          render={(fieldProps) => (
                            <FormikTextField
                              label={t('field.annotation.label')}
                              {...fieldProps}
                              required={false}
                            />
                          )}
                        />
                        <IconButton
                          styles={{
                            root: { color: 'red', marginTop: 'auto' },
                          }}
                          iconProps={{ iconName: 'Delete' }}
                          onClick={() => {
                            helpers.remove(i)
                          }}
                        />
                      </FieldStack>

                      <FieldStack horizontal={true} key={x.key + 100}>
                        <FieldStackItem
                          name={`materials[${i}].isDangerousGood`}
                          required={true}
                          grow={false}
                          render={(fieldProps) => {
                            return (
                              <>
                                <Label required={true}>
                                  {t('field.isDangerousGood.label')}
                                </Label>
                                <Dropdown
                                  defaultValue={fieldProps.field.value || -1}
                                  options={[
                                    { key: 1, text: 'Ja' },
                                    { key: 0, text: 'Nein' },
                                  ]}
                                  onChange={(_, o) =>
                                    handleIsDangerousGoodChange(i, o)
                                  }
                                />
                              </>
                            )
                          }}
                        />
                        {values[i].isDangerousGood && (
                          <FieldStackItem
                            name={`materials[${i}].dangerousGood.unNumber`}
                            grow={0}
                            disableShrink={false}
                            render={(fieldProps) => (
                              <FormikTagPicker
                                label={t('field.unNumber.label')}
                                {...fieldProps}
                                styles={tagPickerStyle}
                                tags={dangerousGoods}
                                required={true}
                                onChange={(item) =>
                                  handleUNNumberChange(item, i)
                                }
                              />
                            )}
                          />
                        )}
                      </FieldStack>
                    </Stack>
                  ))}
                  <FieldStack horizontal={true}>
                    <DefaultButton
                      text={'+ Material'}
                      onClick={() => {
                        helpers.push({
                          material: '',
                          amount: 1,
                          orderId: '',
                          annotation: '',
                          transport: { transportType: '' },
                          key: shortid.generate(),
                        })
                      }}
                    />
                  </FieldStack>
                </AutoStack>
              )
            }}
          </FieldArray>
        </>
      </FlexSpinner>
    </FormStack>
  )
}
